if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.onVariantChangeUnsubscriber = undefined;
        this.onCartUpdateUnsubscriber = undefined;
        this.onCartErrorUnsubscriber = undefined;
        this.mainSubmitObserver = undefined;
        this.visibilityObserver = undefined;
        this.currentVariant = undefined;
        this.currentQuantity = 1;
      }

      connectedCallback() {
        this.productInfo = document.querySelector("product-info[id^='MainProduct']");
        if (!this.productInfo) return;

        this.mainFormElement = this.productInfo.querySelector('product-form');
        this.mainSubmitButton = this.mainFormElement?.querySelector('[type="submit"]');
        this.mainVariantPicker = this.productInfo.querySelector('variant-selects');
        this.mainQuantityInput = this.productInfo.querySelector('.quantity__input[name="quantity"]');
        this.visibilityTarget =
          this.productInfo.querySelector('.product-form__buttons') || this.mainFormElement || this.productInfo;

        this.stickyButton = this.querySelector('[data-sticky-submit]');
        this.changeButton = this.querySelector('[data-sticky-change]');
        this.titleNode = this.querySelector('.sticky-add-to-cart__title');
        this.variantNode = this.querySelector('[data-sticky-variant-text]');
        this.imageNode = this.querySelector('.sticky-add-to-cart__image');
        this.priceCurrentNodes = this.querySelectorAll('[data-sticky-price-current], [data-sticky-mobile-price-current]');
        this.priceCompareNodes = this.querySelectorAll('[data-sticky-price-compare], [data-sticky-mobile-price-compare]');
        this.submitLabelNode = this.querySelector('[data-sticky-submit-text]');

        this.changeButton?.addEventListener('click', this.handleChangeClick);
        this.stickyButton?.addEventListener('click', this.handleStickySubmit);
        this.mainQuantityInput?.addEventListener('change', this.handleQuantityChange);
        this.mainQuantityInput?.addEventListener('input', this.handleQuantityChange);

        this.observeMainSubmit();
        this.observeVisibility();
        this.bindPubSub();
        this.syncFromDom();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.onCartUpdateUnsubscriber?.();
        this.onCartErrorUnsubscriber?.();
        this.mainSubmitObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.changeButton?.removeEventListener('click', this.handleChangeClick);
        this.stickyButton?.removeEventListener('click', this.handleStickySubmit);
        this.mainQuantityInput?.removeEventListener('change', this.handleQuantityChange);
        this.mainQuantityInput?.removeEventListener('input', this.handleQuantityChange);
      }

      bindPubSub() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.productInfo.dataset.section) return;
          const variant = event?.data?.variant;
          if (!variant) return;
          this.syncVariant(variant);
          this.syncButtonState();
        });

        this.onCartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.stickyButton?.classList.remove('loading');
          this.syncButtonState();
        });

        this.onCartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
          this.stickyButton?.classList.remove('loading');
          this.syncButtonState();
        });
      }

      observeMainSubmit() {
        if (!this.mainSubmitButton) return;

        this.mainSubmitObserver = new MutationObserver(() => this.syncFromDom());
        this.mainSubmitObserver.observe(this.mainSubmitButton, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      observeVisibility() {
        if (!this.visibilityTarget) return;

        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            this.classList.toggle('is-visible', !entry.isIntersecting);
          },
          {
            threshold: 0,
            rootMargin: '0px 0px -18% 0px',
          }
        );

        this.visibilityObserver.observe(this.visibilityTarget);
      }

      syncFromDom() {
        this.mainVariantPicker = this.productInfo.querySelector('variant-selects');
        const selectedVariantScript = this.mainVariantPicker?.querySelector('[data-selected-variant]');
        if (selectedVariantScript?.textContent) {
          try {
            const variant = JSON.parse(selectedVariantScript.textContent);
            if (variant) {
              this.syncVariant(variant);
            } else {
              this.currentVariant = null;
              const options = Array.from(this.mainVariantPicker.querySelectorAll('fieldset, select'))
                .map((option) => option.matches('select') ? option.value : option.querySelector('input:checked')?.value)
                .filter(Boolean);
              if (this.variantNode) this.variantNode.textContent = options.join(' / ');
              this.updatePrice();
            }
          } catch (error) {
            console.error(error);
          }
        }

        const mainTitle = this.productInfo.querySelector('h1');
        if (mainTitle && this.titleNode) {
          this.titleNode.textContent = mainTitle.textContent.trim();
        }

        this.syncQuantity();
        this.syncButtonState();
      }

      syncVariant(variant) {
        if (!variant) return;

        this.currentVariant = variant;

        const optionValues = Array.isArray(variant.options) ? variant.options.filter(Boolean) : [];
        if (this.variantNode) {
          this.variantNode.textContent = optionValues.join(' / ');
        }

        if (this.imageNode) {
          const variantImage = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
          if (variantImage) {
            this.imageNode.src = variantImage;
            this.imageNode.srcset = '';
            this.imageNode.alt = variant.name || this.titleNode?.textContent || '';
          }
        }

        this.syncQuantity();
        this.updatePrice();

        this.syncButtonState(variant.available);
      }

      syncQuantity() {
        const quantity = parseInt(this.mainQuantityInput?.value || '1', 10);
        this.currentQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      updatePrice() {
        if (!this.currentVariant) {
          this.priceCurrentNodes?.forEach((node) => { node.hidden = true; });
          this.priceCompareNodes?.forEach((node) => { node.hidden = true; });
          return;
        }

        const price = (parseInt(this.currentVariant.price || 0, 10) || 0) * this.currentQuantity;
        const compareAtPrice = (parseInt(this.currentVariant.compare_at_price || 0, 10) || 0) * this.currentQuantity;

        this.priceCurrentNodes?.forEach((priceNode) => {
          priceNode.hidden = false;
          priceNode.textContent = this.formatMoney(price);
        });

        this.priceCompareNodes?.forEach((priceNode) => {
          const showCompare = this.dataset.showComparePrice === 'true' && compareAtPrice > price;
          priceNode.hidden = !showCompare;
          priceNode.textContent = showCompare ? this.formatMoney(compareAtPrice) : '';
        });
      }

      syncButtonState(variantAvailable) {
        if (!this.stickyButton || !this.mainSubmitButton) return;

        const isDisabled =
          this.mainSubmitButton.hasAttribute('disabled') ||
          this.mainSubmitButton.getAttribute('aria-disabled') === 'true' ||
          variantAvailable === false;

        this.stickyButton.toggleAttribute('disabled', isDisabled);
        this.stickyButton.classList.toggle('loading', this.mainSubmitButton.classList.contains('loading'));
        this.classList.toggle('is-unavailable', isDisabled);

        const mainTextNode =
          this.mainSubmitButton.querySelector('[data-submit-text]') ||
          this.mainSubmitButton.querySelector('span:not(.hidden)') ||
          this.mainSubmitButton;

        if (this.submitLabelNode && mainTextNode) {
          this.submitLabelNode.textContent = mainTextNode.textContent.trim();
        }
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) {
          const moneyFormat = this.dataset.moneyFormat || window.Shopify.money_format;

          try {
            return window.Shopify.formatMoney(cents, moneyFormat);
          } catch (error) {
            console.warn('Sticky add to cart money formatting fallback applied.', error);
          }
        }

        const amount = (parseInt(cents || 0, 10) || 0) / 100;
        const currencyCode =
          document.querySelector('product-form')?.dataset.currencyCode ||
          window.Shopify?.currency?.active ||
          'USD';

        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
          }).format(amount);
        } catch (error) {
          return `$${amount.toFixed(2)}`;
        }
      }

      handleChangeClick = (event) => {
        event.preventDefault();
        this.mainVariantPicker = this.productInfo?.querySelector('variant-selects');
        if (!this.mainVariantPicker) return;

        this.mainVariantPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusTarget =
          this.mainVariantPicker.querySelector('input:not([disabled]), button, select, label[for]');
        focusTarget?.focus?.({ preventScroll: true });
      };

      handleQuantityChange = () => {
        this.syncQuantity();
        this.updatePrice();
      };

      handleStickySubmit = (event) => {
        event.preventDefault();
        if (!this.mainSubmitButton || this.mainSubmitButton.hasAttribute('disabled')) return;

        this.stickyButton?.classList.add('loading');

        const mainForm = this.mainFormElement?.form || this.mainFormElement?.querySelector('form');
        if (mainForm?.requestSubmit) {
          mainForm.requestSubmit(this.mainSubmitButton);
          return;
        }

        this.mainSubmitButton.click();
      };
    }
  );
}
